import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import InsuranceBadge from "@/components/InsuranceBadge";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { SellerBalance } from "@/components/SellerBalance";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Download, MapPin, Package, FileText, Truck, RefreshCw, Loader2, XCircle, CheckCircle2, User, Phone, Car, MessageSquare, Send, Trash2, Clock, Edit2, Save, X, Video, Upload, Play, ExternalLink, Flag, AlertTriangle } from "lucide-react";
import { formatDateIST, formatToIST } from "@/utils/dateUtils";
import { LRCopyInfo } from "@/components/LRCopyInfo";
import OrderEditHistory from "@/components/OrderEditHistory";
import ManifestApiLogs from "@/components/ManifestApiLogs";
import { AppointmentEditRow, ReferenceEditRow } from "@/components/OrderInfoEditors";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS_COLORS = {
  CREATED: "bg-blue-100 text-blue-800",
  MANIFESTED: "bg-purple-100 text-purple-800",
  PICKED_UP: "bg-yellow-100 text-yellow-800",
  LEFT_ORIGIN: "bg-orange-100 text-orange-800",
  REACH_DESTINATION: "bg-cyan-100 text-cyan-800",
  APPOINTMENT_SET: "bg-indigo-100 text-indigo-800",
  APPOINTMENT_UPDATED: "bg-indigo-100 text-indigo-800",
  APPOINTMENT_SCHEDULED: "bg-indigo-100 text-indigo-800",
  UNDEL_REATTEMPT: "bg-amber-100 text-amber-800",
  PART_DEL: "bg-lime-100 text-lime-800",
  OFD: "bg-teal-100 text-teal-800",
  DELIVERED: "bg-green-100 text-green-800",
  RETURNED_INTRANSIT: "bg-red-100 text-red-800",
  RECEIVED_AT_RETURN_CENTER: "bg-pink-100 text-pink-800",
  RETURN_OFD: "bg-rose-100 text-rose-800",
  RETURN_DELIVERED: "bg-violet-100 text-violet-800",
  NOT_PICKED: "bg-gray-100 text-gray-800",
  LOST: "bg-red-200 text-red-900",
  CANCELLED: "bg-gray-200 text-gray-900"
};

const OrderDetail = ({ user, onLogout }) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchingPOD, setFetchingPOD] = useState(false);
  const [fetchingLabel, setFetchingLabel] = useState(null); // Track which label size is being fetched
  const [fetchingLRCopy, setFetchingLRCopy] = useState(false);
  const [extractingLRInfo, setExtractingLRInfo] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [fetchingTracking, setFetchingTracking] = useState(false);
  // Freight re-pull ("Refresh Freight Charges"): preview -> confirm -> apply
  const [freightPreviewing, setFreightPreviewing] = useState(false);
  const [freightPreview, setFreightPreview] = useState(null);
  const [freightRefreshOpen, setFreightRefreshOpen] = useState(false);
  const [freightApplying, setFreightApplying] = useState(false);
  // Rate validation — compare Delhivery's actual charge vs the signed agreement card
  const [rateVal, setRateVal] = useState(null);
  // Pre-LRN order editing
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editPickups, setEditPickups] = useState([]);
  const [editDrops, setEditDrops] = useState([]);
  const [pickupDetail, setPickupDetail] = useState(null); // inline edit of the selected pickup location
  const [dropDetail, setDropDetail] = useState(null);     // inline edit of the selected drop location
  
  // Contact tracking state
  const [contactLogStatuses, setContactLogStatuses] = useState([]);
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [editingPickup, setEditingPickup] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(false);
  const [pickupPerson, setPickupPerson] = useState({ name: "", phone: "", vehicle_number: "" });
  const [deliveryPerson, setDeliveryPerson] = useState({ name: "", phone: "", vehicle_number: "" });
  const [savingPickup, setSavingPickup] = useState(false);
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [newLogEntry, setNewLogEntry] = useState({ contact_type: "pickup", status: "", notes: "" });
  const [addingLog, setAddingLog] = useState(false);
  const chatEndRef = useRef(null);
  
  // Video upload state
  const [uploadingPickupVideo, setUploadingPickupVideo] = useState(false);
  const [uploadingDeliveryVideo, setUploadingDeliveryVideo] = useState(false);
  const pickupVideoRef = useRef(null);
  const deliveryVideoRef = useRef(null);
  
  // PO Expiry Date state
  const [editingPOExpiry, setEditingPOExpiry] = useState(false);
  const [poExpiryDate, setPOExpiryDate] = useState("");
  const [savingPOExpiry, setSavingPOExpiry] = useState(false);

  useEffect(() => {
    fetchOrderDetail();
    fetchContactLogStatuses();
    fetchContactSuggestions();
  }, [orderId]);
  
  // Update local state when order loads
  useEffect(() => {
    if (order) {
      setPickupPerson(order.pickup_person || { name: "", phone: "", vehicle_number: "" });
      setDeliveryPerson(order.delivery_person || { name: "", phone: "", vehicle_number: "" });
      // Set PO expiry date
      if (order.po_expiry_date) {
        const date = new Date(order.po_expiry_date);
        setPOExpiryDate(date.toISOString().split('T')[0]);
      } else {
        setPOExpiryDate("");
      }
    }
  }, [order]);
  
  // Scroll to bottom of chat when new logs are added
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [order?.contact_logs]);

  // Validate the actual freight against the agreement rate card (badge on the card).
  useEffect(() => {
    if (!order?.id || !order?.freight_charges) { setRateVal(null); return; }
    let cancelled = false;
    axios.get(`${API}/rate-validation/order/${order.id}`)
      .then((r) => { if (!cancelled) setRateVal(r.data); })
      .catch(() => { if (!cancelled) setRateVal(null); });
    return () => { cancelled = true; };
  }, [order?.id, order?.freight_charges]);

  const fetchOrderDetail = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      toast.error("Failed to fetch order details");
      navigate('/admin/orders');
    } finally {
      setLoading(false);
    }
  };

  // --- Pre-LRN order editing (locked once an LRN is assigned) ----------------
  const openEditOrder = async () => {
    const invs = (order.invoices && order.invoices.length
      ? order.invoices
      : [{ invoice_number: order.invoice_number || "", invoice_amount: order.invoice_amount ?? "" }]
    ).map((i) => ({ invoice_number: i.invoice_number || "", invoice_amount: i.invoice_amount ?? "" }));
    const boxes = (order.boxes && order.boxes.length ? order.boxes : [{ length: "", width: "", height: "", weight: "", quantity: 1 }])
      .map((b) => ({ length: b.length ?? "", width: b.width ?? "", height: b.height ?? "", weight: b.weight ?? "", quantity: b.quantity ?? 1 }));
    const refs = (order.reference_numbers || []).map((r) => ({ reference_number: r.reference_number || "", date: r.date || "" }));
    setEditForm({
      pickup_location_id: order.pickup_location_id || "",
      drop_location_id: order.drop_location_id || "",
      description: order.description || "",
      // Normalize stored casing (e.g. bulk orders save "prepaid") so the select reflects it.
      payment_mode: String(order.payment_mode || "").toLowerCase() === "cod" ? "COD" : "Prepaid",
      pickup_type: "fm_pickup",
      insurance_option: order.insurance_option || "owner_risk",
      eway_bill_number: order.eway_bill_number || "",
      invoices: invs,
      boxes,
      reference_numbers: refs,
    });
    setPickupDetail(null);
    setDropDetail(null);
    setEditOpen(true);
    // Load the seller's saved pickup/drop locations for the selects, then ALWAYS
    // include the order's CURRENT location as an option — it may not be in the
    // seller's active list (deleted / inactive / cross-seller), but the select
    // must still reflect the order's real location with its full detail.
    let pkList = [];
    let drList = [];
    if (order.seller_id) {
      const [pk, dr] = await Promise.all([
        axios.get(`${API}/pickup-locations/seller/${order.seller_id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/drop-locations/seller/${order.seller_id}`).catch(() => ({ data: [] })),
      ]);
      pkList = pk.data || [];
      drList = dr.data || [];
    }
    const withCurrent = (list, curId, curObj) => {
      const arr = Array.isArray(list) ? [...list] : [];
      if (curId && !arr.some((x) => x.id === curId)) {
        arr.unshift({ ...(curObj || {}), id: curId });
      }
      return arr;
    };
    setEditPickups(withCurrent(pkList, order.pickup_location_id, order.pickup_location));
    setEditDrops(withCurrent(drList, order.drop_location_id, order.drop_location));
  };

  const setInvoice = (i, field, value) => {
    const next = [...editForm.invoices];
    next[i] = { ...next[i], [field]: field === "invoice_number" ? value.replace(/[^A-Za-z0-9/-]/g, "") : value };
    setEditForm({ ...editForm, invoices: next });
  };
  const setBox = (i, field, value) => {
    const next = [...editForm.boxes];
    next[i] = { ...next[i], [field]: value };
    setEditForm({ ...editForm, boxes: next });
  };
  const setRef = (i, field, value) => {
    const next = [...editForm.reference_numbers];
    next[i] = { ...next[i], [field]: value };
    setEditForm({ ...editForm, reference_numbers: next });
  };

  // Inline edit of the SELECTED pickup/drop location (a shared record).
  const openPickupDetail = () => {
    const p = editPickups.find((x) => x.id === editForm.pickup_location_id);
    if (!p) { toast.error("Select a pickup location first"); return; }
    setPickupDetail({
      facility_name: p.facility_name || "", contact_person_name: p.contact_person_name || "",
      contact_phone: p.contact_phone || "", address_line: p.address_line || "",
      city: p.city || "", state: p.state || "", pincode: p.pincode || "",
    });
  };
  const openDropDetail = () => {
    const d = editDrops.find((x) => x.id === editForm.drop_location_id);
    if (!d) { toast.error("Select a drop location first"); return; }
    setDropDetail({
      company_name: d.company_name || "", contact_person_name: d.contact_person_name || "",
      drop_contact: d.drop_contact || "", address_line: d.address_line || "",
      city: d.city || "", state: d.state || "", pincode: d.pincode || "", email: d.email || "",
    });
  };

  const saveEditOrder = async () => {
    if (!editForm.pickup_location_id) { toast.error("Select a pickup location"); return; }
    if (!editForm.drop_location_id) { toast.error("Select a drop location"); return; }
    if (!editForm.description.trim()) { toast.error("Goods description is required"); return; }
    const invs = editForm.invoices.filter((i) => i.invoice_number && i.invoice_amount !== "");
    if (!invs.length) { toast.error("Add at least one invoice with number and amount"); return; }
    const bad = invs.find((i) => !/^[A-Za-z0-9/-]+$/.test(String(i.invoice_number).trim()));
    if (bad) { toast.error(`Invoice number "${bad.invoice_number}" is invalid — letters, digits, / and - only.`); return; }
    const boxes = editForm.boxes.filter((b) => b.length && b.width && b.height && b.weight && b.quantity);
    if (!boxes.length) { toast.error("Add at least one complete box (dimensions, weight, quantity)"); return; }
    const refs = editForm.reference_numbers.filter((r) => r.reference_number && r.reference_number.trim());
    // When editing the drop location's own details, its phone must be a valid 10-digit number.
    if (dropDetail && String(dropDetail.drop_contact || "").replace(/\D/g, "").length < 10) {
      toast.error("Drop location contact must be a valid 10-digit phone number"); return;
    }
    setSavingEdit(true);
    try {
      // Persist the shared location records first (these also update the Locations page).
      if (pickupDetail) await axios.put(`${API}/pickup-locations/${editForm.pickup_location_id}`, pickupDetail);
      if (dropDetail) await axios.put(`${API}/drop-locations/${editForm.drop_location_id}`, dropDetail);
      await axios.put(`${API}/orders/${orderId}`, {
        pickup_location_id: editForm.pickup_location_id,
        drop_location_id: editForm.drop_location_id,
        description: editForm.description.trim(),
        payment_mode: editForm.payment_mode,
        pickup_type: editForm.pickup_type,
        insurance_option: editForm.insurance_option,
        eway_bill_number: editForm.eway_bill_number || null,
        invoices: invs.map((i) => ({ invoice_number: i.invoice_number.trim(), invoice_amount: parseFloat(i.invoice_amount) })),
        boxes: boxes.map((b) => ({ length: parseFloat(b.length), width: parseFloat(b.width), height: parseFloat(b.height), weight: parseFloat(b.weight), quantity: parseInt(b.quantity, 10) || 1 })),
        reference_numbers: refs.map((r) => ({ reference_number: r.reference_number.trim(), date: r.date || null })),
      });
      toast.success("Order updated");
      setEditOpen(false);
      await fetchOrderDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update order");
    } finally {
      setSavingEdit(false);
    }
  };

  const fetchContactLogStatuses = async () => {
    try {
      const response = await axios.get(`${API}/orders/contact-log-statuses`);
      setContactLogStatuses(response.data);
    } catch (error) {
      console.error("Failed to fetch contact log statuses");
    }
  };
  
  const fetchContactSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/orders/contact-suggestions`);
      setContactSuggestions(response.data);
    } catch (error) {
      console.error("Failed to fetch contact suggestions");
    }
  };
  
  const handleSavePickupPerson = async () => {
    if (!pickupPerson.name && !pickupPerson.phone) {
      toast.error("Please enter at least name or phone number");
      return;
    }
    setSavingPickup(true);
    try {
      const response = await axios.put(`${API}/orders/${orderId}/pickup-person`, pickupPerson);
      if (response.data.success) {
        toast.success("Pickup person updated");
        setOrder(prev => ({ ...prev, pickup_person: response.data.pickup_person }));
        setEditingPickup(false);
        fetchContactSuggestions(); // Refresh suggestions
      }
    } catch (error) {
      toast.error("Failed to update pickup person");
    } finally {
      setSavingPickup(false);
    }
  };
  
  const handleSaveDeliveryPerson = async () => {
    if (!deliveryPerson.name && !deliveryPerson.phone) {
      toast.error("Please enter at least name or phone number");
      return;
    }
    setSavingDelivery(true);
    try {
      const response = await axios.put(`${API}/orders/${orderId}/delivery-person`, deliveryPerson);
      if (response.data.success) {
        toast.success("Delivery person updated");
        setOrder(prev => ({ ...prev, delivery_person: response.data.delivery_person }));
        setEditingDelivery(false);
        fetchContactSuggestions(); // Refresh suggestions
      }
    } catch (error) {
      toast.error("Failed to update delivery person");
    } finally {
      setSavingDelivery(false);
    }
  };
  
  const handleAddContactLog = async () => {
    if (!newLogEntry.status || !newLogEntry.notes.trim()) {
      toast.error("Please select status and enter notes");
      return;
    }
    setAddingLog(true);
    try {
      const response = await axios.post(`${API}/orders/${orderId}/contact-log`, newLogEntry);
      if (response.data.success) {
        toast.success("Contact log added");
        setOrder(prev => ({
          ...prev,
          contact_logs: [...(prev.contact_logs || []), response.data.log_entry]
        }));
        setNewLogEntry({ contact_type: "pickup", status: "", notes: "" });
      }
    } catch (error) {
      toast.error("Failed to add contact log");
    } finally {
      setAddingLog(false);
    }
  };
  
  const handleDeleteContactLog = async (logId) => {
    try {
      const response = await axios.delete(`${API}/orders/${orderId}/contact-log/${logId}`);
      if (response.data.success) {
        toast.success("Contact log deleted");
        setOrder(prev => ({
          ...prev,
          contact_logs: prev.contact_logs.filter(log => log.id !== logId)
        }));
      }
    } catch (error) {
      toast.error("Failed to delete contact log");
    }
  };
  
  const applySuggestion = (suggestion, type) => {
    const contact = {
      name: suggestion.name || "",
      phone: suggestion.phone || "",
      vehicle_number: suggestion.vehicle_number || ""
    };
    if (type === "pickup") {
      setPickupPerson(contact);
    } else {
      setDeliveryPerson(contact);
    }
  };
  
  // Video upload handlers
  const handleUploadPickupVideo = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/3gpp', 'video/x-matroska'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Allowed: MP4, MOV, AVI, WebM, 3GP, MKV");
      return;
    }
    
    // Check file size (100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 100MB");
      return;
    }
    
    setUploadingPickupVideo(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/orders/${orderId}/upload-pickup-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        toast.success("Pickup video uploaded successfully");
        setOrder(prev => ({
          ...prev,
          pickup_videos: [...(prev.pickup_videos || []), response.data.video]
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload pickup video");
    } finally {
      setUploadingPickupVideo(false);
      if (pickupVideoRef.current) pickupVideoRef.current.value = '';
    }
  };
  
  const handleUploadDeliveryVideo = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/3gpp', 'video/x-matroska'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Allowed: MP4, MOV, AVI, WebM, 3GP, MKV");
      return;
    }
    
    // Check file size (100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 100MB");
      return;
    }
    
    setUploadingDeliveryVideo(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/orders/${orderId}/upload-delivery-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        toast.success("Delivery video uploaded successfully");
        setOrder(prev => ({
          ...prev,
          delivery_videos: [...(prev.delivery_videos || []), response.data.video]
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload delivery video");
    } finally {
      setUploadingDeliveryVideo(false);
      if (deliveryVideoRef.current) deliveryVideoRef.current.value = '';
    }
  };
  
  const handleDeletePickupVideo = async (videoId) => {
    try {
      const response = await axios.delete(`${API}/orders/${orderId}/pickup-video/${videoId}`);
      if (response.data.success) {
        toast.success("Pickup video deleted");
        setOrder(prev => ({
          ...prev,
          pickup_videos: prev.pickup_videos.filter(v => v.id !== videoId)
        }));
      }
    } catch (error) {
      toast.error("Failed to delete pickup video");
    }
  };
  
  const handleDeleteDeliveryVideo = async (videoId) => {
    try {
      const response = await axios.delete(`${API}/orders/${orderId}/delivery-video/${videoId}`);
      if (response.data.success) {
        toast.success("Delivery video deleted");
        setOrder(prev => ({
          ...prev,
          delivery_videos: prev.delivery_videos.filter(v => v.id !== videoId)
        }));
      }
    } catch (error) {
      toast.error("Failed to delete delivery video");
    }
  };
  
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  // PO Expiry Date handler
  const handleSavePOExpiry = async () => {
    setSavingPOExpiry(true);
    try {
      const response = await axios.put(`${API}/orders/${orderId}/po-expiry-date`, {
        po_expiry_date: poExpiryDate ? new Date(poExpiryDate).toISOString() : null
      });
      if (response.data.success) {
        toast.success("PO Expiry Date updated");
        setOrder(prev => ({ ...prev, po_expiry_date: poExpiryDate ? new Date(poExpiryDate).toISOString() : null }));
        setEditingPOExpiry(false);
      }
    } catch (error) {
      toast.error("Failed to update PO Expiry Date");
    } finally {
      setSavingPOExpiry(false);
    }
  };

  const handleCancelShipment = async () => {
    setCancelling(true);
    try {
      const response = await axios.post(`${API}/orders/${orderId}/cancel`);
      if (response.data.success) {
        toast.success(response.data.message || "Shipment cancelled successfully!");
        await fetchOrderDetail();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel shipment");
    } finally {
      setCancelling(false);
    }
  };

  const handleFetchTracking = async () => {
    setFetchingTracking(true);
    try {
      const response = await axios.post(`${API}/orders/${orderId}/track-status`);
      if (response.data.success) {
        toast.success(response.data.message || "Tracking fetched successfully!");
        // Refresh order details to show the updated data
        await fetchOrderDetail();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch tracking");
    } finally {
      setFetchingTracking(false);
    }
  };

  const handleFetchPOD = async () => {
    setFetchingPOD(true);
    try {
      const response = await axios.post(`${API}/orders/${orderId}/fetch-pod`);
      if (response.data.success) {
        toast.success("POD fetched successfully!");
        // Refresh order details to show the new POD
        await fetchOrderDetail();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch POD");
    } finally {
      setFetchingPOD(false);
    }
  };

  const handleDownloadPOD = async () => {
    setFetchingPOD(true);
    try {
      // Call the documents API to get fresh presigned URL
      const response = await axios.get(`${API}/orders/${orderId}/documents`);
      if (response.data.lm_pod_url) {
        window.open(response.data.lm_pod_url, '_blank');
      } else {
        toast.error("POD not available");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download POD");
    } finally {
      setFetchingPOD(false);
    }
  };

  const handleFetchShippingLabel = async (size) => {
    setFetchingLabel(size);
    try {
      const response = await axios.get(`${API}/orders/${orderId}/shipping-label?size=${size}`);
      if (response.data.url) {
        window.open(response.data.url, '_blank');
        if (!response.data.cached) {
          toast.success(`Shipping label (${size.toUpperCase()}) generated successfully!`);
          // Refresh to cache the URL
          await fetchOrderDetail();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to fetch shipping label (${size})`);
    } finally {
      setFetchingLabel(null);
    }
  };

  const handleFetchLRCopy = async () => {
    setFetchingLRCopy(true);
    try {
      const response = await axios.get(`${API}/orders/${orderId}/lr-copy`);
      if (response.data.url) {
        window.open(response.data.url, '_blank');
        if (!response.data.cached) {
          toast.success("LR Copy fetched successfully!");
          await fetchOrderDetail();
        }
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || "Failed to fetch LR Copy";
      if (status === 404) {
        toast.error(detail);
        await fetchOrderDetail();
      } else {
        toast.error(detail);
      }
    } finally {
      setFetchingLRCopy(false);
    }
  };

  const handleExtractLRInfo = async () => {
    setExtractingLRInfo(true);
    try {
      await axios.post(`${API}/orders/${orderId}/extract-lr-copy-info`);
      toast.success("LR Copy info extracted successfully!");
      await fetchOrderDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to extract LR Copy info");
    } finally {
      setExtractingLRInfo(false);
    }
  };

  const handleDownloadDocument = async (docType) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/download/${docType}`);
      window.open(response.data.download_url, '_blank');
    } catch (error) {
      toast.error("Failed to generate download link");
    }
  };

  const handleFetchFreightCharges = async () => {
    try {
      toast.loading("Fetching freight charges...");
      const response = await axios.post(`${API}/orders/${orderId}/fetch-freight-charges`);
      toast.dismiss();
      toast.success("Freight charges fetched successfully");
      // Refresh order details to show updated charges
      fetchOrderDetail();
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || "Failed to fetch freight charges");
    }
  };

  const handleCalculateBilling = async () => {
    try {
      toast.loading("Calculating seller billing...");
      const response = await axios.post(`${API}/orders/${orderId}/calculate-billing`);
      toast.dismiss();
      toast.success("Billing calculated successfully");
      // Refresh order details to show updated charges
      fetchOrderDetail();
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || "Failed to calculate billing");
    }
  };

  // Step 1: re-pull live Delhivery freight + recompute the seller charge (no money moves),
  // then open the confirm dialog showing old vs new + the wallet impact.
  const handlePreviewFreightRefresh = async () => {
    setFreightPreviewing(true);
    try {
      const { data } = await axios.post(`${API}/orders/${orderId}/freight-refresh/preview`);
      setFreightPreview(data);
      setFreightRefreshOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not fetch live freight charges");
    } finally {
      setFreightPreviewing(false);
    }
  };

  // Step 2: apply — re-pulls fresh, versions the pull (keeping the original), and posts the
  // +/- wallet adjustment. Guarded by the previewed amount so the carrier can't change it under us.
  const handleApplyFreightRefresh = async () => {
    if (!freightPreview) return;
    setFreightApplying(true);
    try {
      const { data } = await axios.post(`${API}/orders/${orderId}/freight-refresh/apply`, {
        expected_new_final_amount: freightPreview.expected_new_final_amount,
      });
      setFreightRefreshOpen(false);
      setFreightPreview(null);
      toast.success(data.message || "Freight charges refreshed");
      fetchOrderDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply freight refresh");
    } finally {
      setFreightApplying(false);
    }
  };

  const money = (v) =>
    (v == null ? "0.00" : Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

  if (loading) {
    return (
      <AdminLayout user={user} onLogout={onLogout}>
        <div className="flex justify-center py-12">
          <div className="text-slate-600">Loading order details...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/orders')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Orders
          </Button>
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-slate-800">{order.order_number}</h1>
            <p className="text-slate-600">Order Details</p>
            {order.insurance_option && <div className="mt-2"><InsuranceBadge value={order.insurance_option} /></div>}
          </div>
          <Badge className={`${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800'} border-0 text-base px-4 py-2`}>
            {order.status.replace(/_/g, ' ')}
          </Badge>

          {/* Edit Order — only before an LRN is assigned (after that the shipment is locked) */}
          {!order.lrn && order.status !== 'CANCELLED' && (
            <Button variant="outline" className="flex items-center gap-2" onClick={openEditOrder} data-testid="edit-order-btn">
              <Edit2 className="w-4 h-4" /> Edit Order
            </Button>
          )}

          {/* Edit Order modal (controlled; portals to body) */}
          <AlertDialog open={editOpen} onOpenChange={setEditOpen}>
            <AlertDialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <AlertDialogHeader>
                <AlertDialogTitle>Edit Order {order.order_number}</AlertDialogTitle>
                <AlertDialogDescription>
                  Allowed only until an LRN is assigned. These fields feed the courier manifest.
                </AlertDialogDescription>
              </AlertDialogHeader>
              {editForm && (
                <div className="space-y-4 text-left">
                  {/* Pickup location — select a different one, or edit the selected one's details */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label>Pickup Location <span className="text-red-500">*</span></Label>
                      <button type="button" className="text-xs text-teal-600 hover:underline" onClick={pickupDetail ? () => setPickupDetail(null) : openPickupDetail}>
                        {pickupDetail ? "Done editing details" : "Edit pickup details"}
                      </button>
                    </div>
                    <Select value={editForm.pickup_location_id} onValueChange={(v) => { setEditForm({ ...editForm, pickup_location_id: v }); setPickupDetail(null); }}>
                      <SelectTrigger><SelectValue placeholder="Select pickup" /></SelectTrigger>
                      <SelectContent>
                        {editPickups.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.facility_name}{p.city ? ` — ${p.city}` : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {pickupDetail && (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2 mt-1">
                        <p className="rounded bg-amber-50 border border-amber-200 px-2 py-1 text-[11px] text-amber-800">
                          Updates the saved pickup location — it will also change on the Pickup Locations page and any other order using it.
                        </p>
                        <Input value={pickupDetail.facility_name} onChange={(e) => setPickupDetail({ ...pickupDetail, facility_name: e.target.value })} placeholder="Facility name" />
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={pickupDetail.contact_person_name} onChange={(e) => setPickupDetail({ ...pickupDetail, contact_person_name: e.target.value })} placeholder="Contact person" />
                          <Input value={pickupDetail.contact_phone} onChange={(e) => setPickupDetail({ ...pickupDetail, contact_phone: e.target.value })} placeholder="Contact phone" />
                        </div>
                        <Input value={pickupDetail.address_line} onChange={(e) => setPickupDetail({ ...pickupDetail, address_line: e.target.value })} placeholder="Address" />
                        <div className="grid grid-cols-3 gap-2">
                          <Input value={pickupDetail.city} onChange={(e) => setPickupDetail({ ...pickupDetail, city: e.target.value })} placeholder="City" />
                          <Input value={pickupDetail.state} onChange={(e) => setPickupDetail({ ...pickupDetail, state: e.target.value })} placeholder="State" />
                          <Input value={pickupDetail.pincode} onChange={(e) => setPickupDetail({ ...pickupDetail, pincode: e.target.value })} placeholder="Pincode" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Drop location — select a different one, or edit the selected one's details */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label>Drop Location <span className="text-red-500">*</span></Label>
                      <button type="button" className="text-xs text-teal-600 hover:underline" onClick={dropDetail ? () => setDropDetail(null) : openDropDetail}>
                        {dropDetail ? "Done editing details" : "Edit drop details"}
                      </button>
                    </div>
                    <Select value={editForm.drop_location_id} onValueChange={(v) => { setEditForm({ ...editForm, drop_location_id: v }); setDropDetail(null); }}>
                      <SelectTrigger><SelectValue placeholder="Select drop" /></SelectTrigger>
                      <SelectContent>
                        {editDrops.map((d) => (
                          <SelectItem key={d.id} value={d.id}>{d.company_name || d.contact_person_name || "Drop"}{d.city ? ` — ${d.city}` : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {dropDetail && (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2 mt-1">
                        <p className="rounded bg-amber-50 border border-amber-200 px-2 py-1 text-[11px] text-amber-800">
                          Updates the saved drop location — it will also change on the Drop Locations page and any other order using it.
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={dropDetail.company_name} onChange={(e) => setDropDetail({ ...dropDetail, company_name: e.target.value })} placeholder="Company name" />
                          <Input value={dropDetail.contact_person_name} onChange={(e) => setDropDetail({ ...dropDetail, contact_person_name: e.target.value })} placeholder="Contact person" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={dropDetail.drop_contact} onChange={(e) => setDropDetail({ ...dropDetail, drop_contact: e.target.value })} placeholder="Phone (10-digit)" />
                          <Input value={dropDetail.email} onChange={(e) => setDropDetail({ ...dropDetail, email: e.target.value })} placeholder="Email (optional)" />
                        </div>
                        <Input value={dropDetail.address_line} onChange={(e) => setDropDetail({ ...dropDetail, address_line: e.target.value })} placeholder="Address" />
                        <div className="grid grid-cols-3 gap-2">
                          <Input value={dropDetail.city} onChange={(e) => setDropDetail({ ...dropDetail, city: e.target.value })} placeholder="City" />
                          <Input value={dropDetail.state} onChange={(e) => setDropDetail({ ...dropDetail, state: e.target.value })} placeholder="State" />
                          <Input value={dropDetail.pincode} onChange={(e) => setDropDetail({ ...dropDetail, pincode: e.target.value })} placeholder="Pincode" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label>Description <span className="text-red-500">*</span></Label>
                    <Input value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} placeholder="Goods description (e.g. Apparel)" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Payment Mode</Label>
                      <Select value={editForm.payment_mode} onValueChange={(v) => setEditForm({ ...editForm, payment_mode: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="Prepaid">Prepaid</SelectItem><SelectItem value="COD">COD</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>Insurance</Label>
                      <Select value={editForm.insurance_option} onValueChange={(v) => setEditForm({ ...editForm, insurance_option: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="owner_risk">Owner Risk</SelectItem><SelectItem value="carrier_insurance">Carrier Insurance</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>Pickup Type</Label>
                      <Select value={editForm.pickup_type} onValueChange={(v) => setEditForm({ ...editForm, pickup_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="fm_pickup">FM Pickup</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1 col-span-2">
                      <Label>E-Way Bill No.</Label>
                      <Input value={editForm.eway_bill_number} onChange={(e) => setEditForm({ ...editForm, eway_bill_number: e.target.value })} placeholder="Optional" />
                    </div>
                  </div>

                  {/* Invoices */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Invoices <span className="text-red-500">*</span></Label>
                      <Button type="button" size="sm" variant="outline" onClick={() => setEditForm({ ...editForm, invoices: [...editForm.invoices, { invoice_number: "", invoice_amount: "" }] })}>Add</Button>
                    </div>
                    {editForm.invoices.map((inv, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <Input value={inv.invoice_number} onChange={(e) => setInvoice(i, "invoice_number", e.target.value)} placeholder="Invoice no." />
                        <Input type="number" step="0.01" value={inv.invoice_amount} onChange={(e) => setInvoice(i, "invoice_amount", e.target.value)} placeholder="Amount" className="w-32" />
                        {editForm.invoices.length > 1 && (
                          <Button type="button" size="icon" variant="ghost" onClick={() => setEditForm({ ...editForm, invoices: editForm.invoices.filter((_, idx) => idx !== i) })}><X className="w-4 h-4" /></Button>
                        )}
                      </div>
                    ))}
                    <p className="text-[11px] text-slate-400">Invoice number: letters, digits, / and - only.</p>
                  </div>

                  {/* Boxes */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Boxes <span className="text-red-500">*</span></Label>
                      <Button type="button" size="sm" variant="outline" onClick={() => setEditForm({ ...editForm, boxes: [...editForm.boxes, { length: "", width: "", height: "", weight: "", quantity: 1 }] })}>Add</Button>
                    </div>
                    <div className="grid grid-cols-[repeat(5,1fr)_auto] gap-2 text-[11px] text-slate-400 px-1">
                      <span>L (cm)</span><span>W (cm)</span><span>H (cm)</span><span>Wt (kg)</span><span>Qty</span><span></span>
                    </div>
                    {editForm.boxes.map((b, i) => (
                      <div key={i} className="grid grid-cols-[repeat(5,1fr)_auto] gap-2 items-center">
                        <Input type="number" step="0.01" value={b.length} onChange={(e) => setBox(i, "length", e.target.value)} />
                        <Input type="number" step="0.01" value={b.width} onChange={(e) => setBox(i, "width", e.target.value)} />
                        <Input type="number" step="0.01" value={b.height} onChange={(e) => setBox(i, "height", e.target.value)} />
                        <Input type="number" step="0.01" value={b.weight} onChange={(e) => setBox(i, "weight", e.target.value)} />
                        <Input type="number" min="1" value={b.quantity} onChange={(e) => setBox(i, "quantity", e.target.value)} />
                        {editForm.boxes.length > 1 ? (
                          <Button type="button" size="icon" variant="ghost" onClick={() => setEditForm({ ...editForm, boxes: editForm.boxes.filter((_, idx) => idx !== i) })}><X className="w-4 h-4" /></Button>
                        ) : <span />}
                      </div>
                    ))}
                  </div>

                  {/* Reference numbers (optional) */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Reference Numbers</Label>
                      <Button type="button" size="sm" variant="outline" onClick={() => setEditForm({ ...editForm, reference_numbers: [...editForm.reference_numbers, { reference_number: "", date: "" }] })}>Add</Button>
                    </div>
                    {editForm.reference_numbers.map((r, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <Input value={r.reference_number} onChange={(e) => setRef(i, "reference_number", e.target.value)} placeholder="Reference no." />
                        <Input type="date" value={r.date || ""} onChange={(e) => setRef(i, "date", e.target.value)} className="w-44" />
                        <Button type="button" size="icon" variant="ghost" onClick={() => setEditForm({ ...editForm, reference_numbers: editForm.reference_numbers.filter((_, idx) => idx !== i) })}><X className="w-4 h-4" /></Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <AlertDialogFooter>
                <Button variant="outline" onClick={() => setEditOpen(false)} disabled={savingEdit}>Cancel</Button>
                <Button onClick={saveEditOrder} disabled={savingEdit} data-testid="edit-order-save">
                  {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Changes
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Fetch Tracking Button - Show only for Delhivery orders with LRN */}
          {order.lrn && order.shipping_partner_name?.toLowerCase() === 'delhivery' && (
            <Button 
              variant="outline" 
              className="flex items-center gap-2 border-[#00A699] text-[#00A699] hover:bg-[#00A699] hover:text-white"
              onClick={handleFetchTracking}
              disabled={fetchingTracking}
            >
              {fetchingTracking ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              {fetchingTracking ? 'Fetching...' : 'Fetch Tracking'}
            </Button>
          )}
          
          {/* Cancel Shipment Button - Show only if order has LRN and status is CREATED or MANIFESTED */}
          {order.lrn && ['CREATED', 'MANIFESTED'].includes(order.status) && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="flex items-center gap-2" disabled={cancelling}>
                  {cancelling ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  Cancel Shipment
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel Shipment?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will cancel the shipment with Delhivery for LRN: <strong>{order.lrn}</strong>.
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>No, Keep Shipment</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleCancelShipment}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Yes, Cancel Shipment
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {/* Cancel Order — for orders WITHOUT an LRN (nothing booked with a courier). Cancels in EdgeLMS only. */}
          {!order.lrn && order.status !== 'CANCELLED' && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="flex items-center gap-2" disabled={cancelling} data-testid="cancel-order-btn">
                  {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                  Cancel Order
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel Order?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This order has no LRN, so nothing was booked with any courier — it will be cancelled in EdgeLMS only.
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>No, Keep Order</AlertDialogCancel>
                  <AlertDialogAction onClick={handleCancelShipment} className="bg-red-600 hover:bg-red-700">
                    Yes, Cancel Order
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Order Information */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" style={{color: '#00A699'}} />
                Order Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Order Number</p>
                  <p className="font-semibold text-slate-900">{order.order_number}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">LRN</p>
                  <p className="font-semibold text-slate-900">{order.lrn || "Not Assigned"}</p>
                </div>
                {order.master_waybill && (
                  <div>
                    <p className="text-sm text-slate-500">Master AWB</p>
                    <p className="font-semibold text-slate-900">{order.master_waybill}</p>
                  </div>
                )}
                {order.waybills && order.waybills.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-500">Box AWBs ({order.waybills.length})</p>
                    <div className="space-y-1">
                      {order.waybills.map((awb, index) => (
                        <p key={index} className="font-semibold text-slate-900 text-sm">{awb}</p>
                      ))}
                    </div>
                  </div>
                )}
                {order.doc_waybill && (
                  <div>
                    <p className="text-sm text-slate-500">Document AWB</p>
                    <p className="font-semibold text-slate-900">{order.doc_waybill}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-500">Seller</p>
                  <p className="font-semibold text-slate-900">{order.seller_brand_name || order.seller_id}</p>
                  <SellerBalance wallet={order.seller_wallet} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">B2B Marketplace</p>
                  {order.b2b_marketplace_name ? (
                    <div className="flex items-center gap-2">
                      {order.b2b_marketplace_logo && (
                        <img src={order.b2b_marketplace_logo} alt={order.b2b_marketplace_name} className="h-5 w-5 object-contain" />
                      )}
                      <p className="font-semibold text-slate-900">{order.b2b_marketplace_name}</p>
                    </div>
                  ) : (
                    <p className="font-semibold text-slate-900">N/A</p>
                  )}
                </div>
                <ReferenceEditRow order={order} onSaved={(patch) => setOrder((prev) => ({ ...prev, ...patch }))} />
                <AppointmentEditRow order={order} onSaved={(patch) => setOrder((prev) => ({ ...prev, ...patch }))} />
                <div>
                  <p className="text-sm text-slate-500">Created Date</p>
                  <p className="font-semibold text-slate-900">
                    {formatDateIST(order.created_at, 'full')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Created By</p>
                  <p className="font-semibold text-slate-900">{order.created_by?.user_name || "N/A"}</p>
                </div>
                {/* PO Expiry Date */}
                <div className="col-span-2 border-t pt-4 mt-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-500">PO Expiry Date</p>
                    {!editingPOExpiry ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingPOExpiry(true)}
                        className="h-6 text-xs"
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        {order.po_expiry_date ? 'Edit' : 'Add'}
                      </Button>
                    ) : (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleSavePOExpiry}
                          disabled={savingPOExpiry}
                          className="h-6 text-green-600"
                        >
                          {savingPOExpiry ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingPOExpiry(false);
                            setPOExpiryDate(order.po_expiry_date ? new Date(order.po_expiry_date).toISOString().split('T')[0] : "");
                          }}
                          className="h-6 text-slate-500"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {editingPOExpiry ? (
                    <Input
                      type="date"
                      value={poExpiryDate}
                      onChange={(e) => setPOExpiryDate(e.target.value)}
                      className="h-9 mt-1 max-w-[200px]"
                    />
                  ) : (
                    <p className={`font-semibold ${order.po_expiry_date ? 'text-slate-900' : 'text-slate-400 italic'}`}>
                      {order.po_expiry_date ? formatDateIST(order.po_expiry_date, 'short') : 'Not Set'}
                    </p>
                  )}
                </div>
              </div>
              {order.description && (
                <div>
                  <p className="text-sm text-slate-500">Description</p>
                  <p className="text-slate-900">{order.description}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* LR Copy & Info - Right column top */}
          {order.lrn && (
            <Card className="border-0 shadow-lg" data-testid="lr-copy-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" style={{color: '#00A699'}} />
                  LR Copy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {order.lr_copy_status === 'not_available' && !order.lr_copy_url ? (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="lr-copy-not-available">
                    <XCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-sm text-amber-700">LR Copy Not Available</span>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleFetchLRCopy()}
                    disabled={fetchingLRCopy}
                  >
                    {fetchingLRCopy ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {order.lr_copy_url ? 'Loading...' : 'Fetching LR Copy...'}
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download LR Copy {!order.lr_copy_url && '(Fetch)'}
                      </>
                    )}
                  </Button>
                )}

                {order.InfoFromPartnerLRCopy ? (
                  <>
                    <LRCopyInfo data={order.InfoFromPartnerLRCopy} />
                    {order.lr_copy_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-center text-slate-500 hover:text-teal-600 hover:border-teal-600"
                        onClick={handleExtractLRInfo}
                        disabled={extractingLRInfo}
                        data-testid="refetch-lr-info-btn"
                      >
                        {extractingLRInfo ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            Re-extracting...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                            Re-extract Info from LR Copy
                          </>
                        )}
                      </Button>
                    )}
                  </>
                ) : order.lr_copy_url ? (
                  <Button
                    variant="outline"
                    className="w-full justify-start border-teal-600 text-teal-600 hover:bg-teal-50"
                    onClick={handleExtractLRInfo}
                    disabled={extractingLRInfo}
                    data-testid="extract-lr-info-btn"
                  >
                    {extractingLRInfo ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Extracting LR Copy Info...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Extract Info from LR Copy
                      </>
                    )}
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          )}

          {/* Invoice Information */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" style={{color: '#00A699'}} />
                Invoice & Shipment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Invoices List */}
              {order.invoices && order.invoices.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-700">Invoices ({order.invoices.length})</p>
                  <div className="border rounded-lg divide-y">
                    {order.invoices.map((inv, index) => (
                      <div key={index} className="flex justify-between items-center p-3">
                        <span className="font-medium text-slate-900">{inv.invoice_number}</span>
                        <span className="text-slate-700">₹{inv.invoice_amount?.toLocaleString()}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center p-3 bg-slate-50 font-semibold">
                      <span className="text-slate-700">Total Invoice Amount</span>
                      <span className="text-slate-900">₹{(order.total_invoice_amount || order.invoice_amount || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ) : order.invoice_number ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Invoice Number</p>
                    <p className="font-semibold text-slate-900">{order.invoice_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Invoice Amount</p>
                    <p className="font-semibold text-slate-900">₹{(order.total_invoice_amount || order.invoice_amount || 0).toLocaleString()}</p>
                  </div>
                </div>
              ) : null}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Payment Mode</p>
                  <p className="font-semibold text-slate-900">{order.payment_mode}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Number of Boxes</p>
                  <p className="font-semibold text-slate-900">{order.number_of_boxes}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Freight Mode</p>
                  <p className="font-semibold text-slate-900">{order.freight_mode === 'FOD' ? 'FOD - Freight on Delivery' : order.freight_mode === 'BTC' ? 'BTC - Bill to Customer' : (order.freight_mode || 'N/A')}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pickup Type</p>
                  <p className="font-semibold text-slate-900">{order.pickup_type === 'fm_pickup' ? 'FM Pickup' : order.pickup_type === 'self_drop' ? 'Self Drop' : (order.pickup_type || 'N/A')}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Insurance</p>
                  <p className="font-semibold text-slate-900">{order.insurance_option === 'carrier_insurance' ? 'Carrier Insurance' : order.insurance_option === 'owner_risk' ? 'Owner Risk' : (order.insurance_option || 'N/A')}</p>
                </div>
                {!order.skip_eway_bill && order.eway_bill_number && (
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">E-Way Bill Number</p>
                    <p className="font-semibold text-slate-900">{order.eway_bill_number}</p>
                  </div>
                )}
              </div>
              <div className="border-t pt-4 space-y-2">
                {order.invoice_document_path && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleDownloadDocument('invoice')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Invoice Document
                  </Button>
                )}
                {order.secondary_document_path && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleDownloadDocument('secondary')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Secondary Document
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Shipping Documents */}
          {order.lrn && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" style={{color: '#00A699'}} />
                  Shipping Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Shipping Labels Section */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Shipping Labels</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { size: 'sm', label: '3" x 2"' },
                        { size: 'md', label: '4" x 2"' },
                        { size: 'a4', label: 'A4 Size' },
                        { size: 'std', label: '4" x 2.5"' }
                      ].map(({ size, label }) => {
                        const hasLabel = order[`shipping_label_${size}_url`];
                        return (
                          <Button
                            key={size}
                            variant="outline"
                            className="justify-start"
                            onClick={() => handleFetchShippingLabel(size)}
                            disabled={fetchingLabel === size}
                          >
                            {fetchingLabel === size ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {hasLabel ? 'Loading...' : 'Generating...'}
                              </>
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-2" />
                                {label} {hasLabel ? '' : '(Generate)'}
                              </>
                            )}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* POD */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Proof of Delivery (POD)</h4>
                    {order.lm_pod_url ? (
                      <Button
                        variant="outline"
                        className="w-full justify-start"
                        onClick={handleDownloadPOD}
                        disabled={fetchingPOD}
                      >
                        {fetchingPOD ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Download POD
                          </>
                        )}
                      </Button>
                    ) : order.status === 'DELIVERED' ? (
                      <Button
                        variant="outline"
                        className="w-full justify-start border-teal-600 text-teal-600 hover:bg-teal-50"
                        onClick={handleFetchPOD}
                        disabled={fetchingPOD}
                      >
                        {fetchingPOD ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Fetching POD...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Fetch POD from Shipping Partner
                          </>
                        )}
                      </Button>
                    ) : (
                      <p className="text-sm text-slate-500">POD available after delivery</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Freight Charges */}
          {order.lrn && ['DELIVERED', 'RETURN_DELIVERED', 'LOST'].includes(order.status) && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Truck className="w-5 h-5" style={{color: '#00A699'}} />
                    Freight Charges
                    {rateVal && rateVal.validated && rateVal.has_mismatch && (
                      <button
                        type="button"
                        onClick={() => navigate('/admin/rate-errors')}
                        title="Delhivery's charge diverges from the agreement rate card — open Rate Errors"
                        className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 text-xs font-medium px-2 py-0.5 hover:bg-red-200"
                      >
                        <AlertTriangle className="w-3 h-3" />
                        Rate error {rateVal.net_overcharge > 0 ? '+' : ''}₹{Number(rateVal.net_overcharge || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    )}
                    {rateVal && rateVal.validated && !rateVal.has_mismatch && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 text-green-700 text-xs font-medium px-2 py-0.5"
                        title={`Matches ${rateVal.card_title || 'agreement'} (${rateVal.lane?.origin_zone}→${rateVal.lane?.dest_zone})`}>
                        <CheckCircle2 className="w-3 h-3" /> Rate OK
                      </span>
                    )}
                    {rateVal && !rateVal.validated && (
                      <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-500 text-xs px-2 py-0.5"
                        title={`Not validated: ${rateVal.reason || 'unknown'}`}>
                        Rate: n/a
                      </span>
                    )}
                  </div>
                  {!order.freight_charges ? (
                    <Button
                      size="sm"
                      onClick={handleFetchFreightCharges}
                      style={{backgroundColor: '#00A699'}}
                      className="hover:opacity-90"
                    >
                      Fetch Charges
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handlePreviewFreightRefresh}
                      disabled={freightPreviewing}
                      className="gap-1.5"
                      title="Re-pull live Delhivery freight and reconcile the seller's wallet if the charge changed"
                      data-testid="refresh-freight-charges"
                    >
                      {freightPreviewing
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <RefreshCw className="w-4 h-4" />}
                      Refresh
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {order.freight_charges ? (
                  <div className="space-y-4">
                    {/* Total Amount */}
                    <div className="bg-[#00A699] bg-opacity-10 rounded-lg p-4 border border-[#00A699]">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-slate-600">Total Charge</span>
                        <span className="text-2xl font-bold" style={{color: '#00A699'}}>
                          ₹{order.freight_charges.total?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                        <div>
                          <span className="text-slate-500">Charged Weight: </span>
                          <span className="font-semibold text-slate-900">{order.freight_charges.charged_wt} kg</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Min Weight: </span>
                          <span className="font-semibold text-slate-900">{order.freight_charges.min_charged_wt} kg</span>
                        </div>
                      </div>
                    </div>

                    {/* Forward Charges */}
                    {order.freight_charges.fwd_price_breakup && (
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-3">Forward Charges Breakup</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-slate-600">Base Freight Charge</span>
                            <span className="font-semibold">₹{order.freight_charges.fwd_price_breakup.base_freight_charge?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-slate-600">Fuel Surcharge</span>
                            <span className="font-semibold">₹{order.freight_charges.fwd_price_breakup.fuel_surcharge?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-slate-600">Insurance ROV</span>
                            <span className="font-semibold">₹{order.freight_charges.fwd_price_breakup.insurance_rov?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-slate-600">First Mile (FM)</span>
                            <span className="font-semibold">₹{order.freight_charges.fwd_price_breakup.fm?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-slate-600">Last Mile (LM)</span>
                            <span className="font-semibold">₹{order.freight_charges.fwd_price_breakup.lm?.toFixed(2)}</span>
                          </div>
                          {order.freight_charges.fwd_price_breakup.oda && (
                            <div className="flex justify-between py-2 border-b">
                              <span className="text-slate-600">ODA Charges (FM + LM)</span>
                              <span className="font-semibold">
                                ₹{((order.freight_charges.fwd_price_breakup.oda.fm || 0) + 
                                   (order.freight_charges.fwd_price_breakup.oda.lm || 0)).toFixed(2)}
                              </span>
                            </div>
                          )}
                          
                          {/* Meta Charges (Handling Charges Breakdown) */}
                          {order.freight_charges.fwd_price_breakup.meta_charges && 
                           Object.keys(order.freight_charges.fwd_price_breakup.meta_charges).some(key => 
                             order.freight_charges.fwd_price_breakup.meta_charges[key] > 0
                           ) && (
                            <div className="bg-amber-50 rounded p-4 my-2">
                              <h5 className="font-semibold text-slate-700 mb-2 text-sm">Handling Charges Breakdown:</h5>
                              <div className="space-y-1">
                                {Object.entries(order.freight_charges.fwd_price_breakup.meta_charges).map(([key, value]) => (
                                  value > 0 && (
                                    <div key={key} className="flex justify-between py-1 text-sm">
                                      <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
                                      <span className="font-semibold">₹{value?.toFixed(2)}</span>
                                    </div>
                                  )
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-slate-600">Other Handling Charges Total</span>
                            <span className="font-semibold">₹{order.freight_charges.fwd_price_breakup.other_handling_charges?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-slate-600">Pre-Tax Freight</span>
                            <span className="font-semibold">₹{order.freight_charges.fwd_price_breakup.pre_tax_freight?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-slate-600">GST ({order.freight_charges.fwd_price_breakup.gst_percent}%)</span>
                            <span className="font-semibold">₹{order.freight_charges.fwd_price_breakup.gst?.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* RTO Charges (for Return Delivered) */}
                    {order.freight_charges.rto_price_breakup && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="font-semibold text-slate-800 mb-3">RTO (Return) Charges Breakup</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-slate-600">Base Freight Charge</span>
                            <span className="font-semibold">₹{order.freight_charges.rto_price_breakup.base_freight_charge?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-slate-600">Fuel Surcharge</span>
                            <span className="font-semibold">₹{order.freight_charges.rto_price_breakup.fuel_surcharge?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-slate-600">Pre-Tax Freight</span>
                            <span className="font-semibold">₹{order.freight_charges.rto_price_breakup.pre_tax_freight?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b">
                            <span className="text-slate-600">GST ({order.freight_charges.rto_price_breakup.gst_percent}%)</span>
                            <span className="font-semibold">₹{order.freight_charges.rto_price_breakup.gst?.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Fetched At / last refreshed */}
                    {order.freight_charges_fetched_at && (
                      <div className="text-xs text-slate-500 mt-3">
                        {order.last_freight_refresh_at ? "Last refreshed on: " : "Fetched on: "}
                        {formatDateIST(order.freight_charges_fetched_at, 'full')}
                      </div>
                    )}

                    {/* Freight pull history — every re-pull that changed the seller charge is
                        versioned here; the original charge is never removed. */}
                    {Array.isArray(order.freight_pull_history) && order.freight_pull_history.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                          <Clock className="w-4 h-4" style={{color: '#00A699'}} />
                          Freight Pull History
                        </h4>
                        <div className="overflow-x-auto -mx-2 px-2">
                          <table className="w-full text-xs min-w-[520px]">
                            <thead>
                              <tr className="text-slate-500 border-b">
                                <th className="text-left py-2 pr-3 font-medium">Pulled</th>
                                <th className="text-left py-2 pr-3 font-medium">Source</th>
                                <th className="text-right py-2 pr-3 font-medium">Delhivery</th>
                                <th className="text-right py-2 pr-3 font-medium">Seller Charge</th>
                                <th className="text-right py-2 font-medium">Wallet Adj.</th>
                              </tr>
                            </thead>
                            <tbody>
                              {order.freight_pull_history.map((h) => (
                                <tr key={h.id} className={`border-b last:border-0 ${h.is_current ? "bg-[#00A699]/5" : ""}`}>
                                  <td className="py-2 pr-3 whitespace-nowrap text-slate-600">
                                    {h.pulled_at ? formatDateIST(h.pulled_at, 'full') : "—"}
                                    {h.is_current && <span className="ml-1.5 text-[#00A699] font-semibold">(current)</span>}
                                  </td>
                                  <td className="py-2 pr-3 whitespace-nowrap capitalize text-slate-600">
                                    {h.source === "admin_refresh" ? "Refresh" : "Original"}
                                  </td>
                                  <td className="py-2 pr-3 text-right tabular-nums">₹{money(h.delhivery_total)}</td>
                                  <td className="py-2 pr-3 text-right tabular-nums font-medium">₹{money(h.seller_final_amount)}</td>
                                  <td className="py-2 text-right tabular-nums">
                                    {h.wallet_adjustment ? (
                                      <span className={h.wallet_adjustment.type === "debit" ? "text-red-600" : "text-green-600"}>
                                        {h.wallet_adjustment.type === "debit" ? "−" : "+"}₹{money(h.wallet_adjustment.amount)}
                                        <span className="text-slate-400"> {h.wallet_adjustment.type === "debit" ? "debited" : "credited"}</span>
                                      </span>
                                    ) : <span className="text-slate-400">—</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-slate-500 mb-3">Freight charges not yet fetched</p>
                    <p className="text-sm text-slate-400">Click &quot;Fetch Charges&quot; button above to retrieve billing details</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Freight refresh — preview + confirm dialog (re-pull + reconcile wallet) */}
          <AlertDialog open={freightRefreshOpen} onOpenChange={setFreightRefreshOpen}>
            <AlertDialogContent className="max-w-lg">
              <AlertDialogHeader>
                <AlertDialogTitle>Refresh Freight Charges</AlertDialogTitle>
                <AlertDialogDescription>
                  {freightPreview?.changed
                    ? "Delhivery's freight for this LRN has changed. Review the impact before applying."
                    : "Delhivery's freight was re-pulled — the seller charge is unchanged."}
                </AlertDialogDescription>
              </AlertDialogHeader>

              {freightPreview && (
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-3 gap-2 rounded-lg border p-3">
                    <div className="text-slate-500 text-xs font-medium"></div>
                    <div className="text-slate-500 text-xs font-medium text-right">Current</div>
                    <div className="text-slate-500 text-xs font-medium text-right">New</div>

                    <div className="text-slate-600">Delhivery total</div>
                    <div className="text-right tabular-nums">₹{money(freightPreview.old?.delhivery_total)}</div>
                    <div className="text-right tabular-nums font-medium">₹{money(freightPreview.new?.delhivery_total)}</div>

                    <div className="text-slate-600">Seller charge</div>
                    <div className="text-right tabular-nums">₹{money(freightPreview.old?.seller_final_amount)}</div>
                    <div className="text-right tabular-nums font-semibold" style={{color: '#00A699'}}>₹{money(freightPreview.new?.seller_final_amount)}</div>
                  </div>

                  {freightPreview.changed ? (
                    <div className={`rounded-lg p-3 ${freightPreview.direction === "debit" ? "bg-red-50 border border-red-200" : "bg-green-50 border border-green-200"}`}>
                      {freightPreview.billed ? (
                        <>
                          <div className="flex justify-between font-medium">
                            <span>{freightPreview.direction === "debit" ? "Additional debit from" : "Credit back to"} {freightPreview.seller_name || "seller"}</span>
                            <span className={freightPreview.direction === "debit" ? "text-red-600" : "text-green-600"}>
                              {freightPreview.direction === "debit" ? "−" : "+"}₹{money(Math.abs(freightPreview.difference))}
                            </span>
                          </div>
                          <div className="flex justify-between text-xs text-slate-500 mt-1">
                            <span>Wallet balance</span>
                            <span>₹{money(freightPreview.wallet?.balance)} → ₹{money(freightPreview.wallet?.projected_balance_after)}</span>
                          </div>
                        </>
                      ) : (
                        <div className="text-slate-600">This order isn't billed yet — the new charge will apply at billing. No wallet movement now.</div>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg bg-slate-50 border p-3 text-slate-600">
                      No wallet adjustment needed. Applying will not change anything.
                    </div>
                  )}
                </div>
              )}

              <AlertDialogFooter>
                <AlertDialogCancel disabled={freightApplying}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => { e.preventDefault(); handleApplyFreightRefresh(); }}
                  disabled={freightApplying || !freightPreview?.changed}
                  style={{backgroundColor: '#00A699'}}
                  className="hover:opacity-90"
                  data-testid="confirm-freight-refresh"
                >
                  {freightApplying ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                  {freightPreview?.changed
                    ? (freightPreview?.billed
                        ? `Confirm & ${freightPreview?.direction === "debit" ? "debit" : "credit"} ₹${money(Math.abs(freightPreview?.difference))}`
                        : "Confirm & update charge")
                    : "No change"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Calculate Billing Button (if freight charges exist but billing not done) */}
          {order.freight_charges && !order.seller_charges && ['DELIVERED', 'RETURN_DELIVERED', 'LOST'].includes(order.status) && (
            <Card className="border-0 shadow-lg border-l-4 border-amber-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-slate-900">Seller Billing Not Calculated</h4>
                    <p className="text-sm text-slate-600 mt-1">
                      Freight charges are available, but seller billing has not been calculated yet.
                    </p>
                  </div>
                  <Button onClick={handleCalculateBilling} style={{backgroundColor: '#00A699'}}>
                    Calculate Billing
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Seller Charges/Billing */}
          {order.seller_charges && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <svg className="w-5 h-5" style={{color: '#00A699'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  Seller Billing Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Total Summary Box */}
                  <div className="bg-gradient-to-r from-[#00A699] to-[#008080] rounded-lg p-4 text-white">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm opacity-90">Amount Charged to Seller</p>
                        <p className="text-3xl font-bold">
                          ₹{order.seller_charges.final_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                      {order.wallet_transaction_id && (
                        <div className="text-right">
                          <p className="text-xs opacity-75">Deducted from wallet</p>
                          <p className="text-sm">{formatDateIST(order.wallet_deducted_at, 'full')}</p>
                        </div>
                      )}
                    </div>
                    {order.seller_charges.charged_wt_used && (
                      <div className="mt-3 pt-3 border-t border-white/20">
                        <span className="text-sm opacity-90">Charged Weight: </span>
                        <span className="font-semibold">{order.seller_charges.charged_wt_used} kg</span>
                      </div>
                    )}
                  </div>

                  {/* Forward Charges */}
                  {order.seller_charges.fwd_charges && (
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-bold">FORWARD</span>
                        Forward Charges Breakdown
                      </h4>
                      
                      <div className="space-y-3 pl-4 border-l-2 border-blue-200">
                        {/* Pre-Tax Charges */}
                        <div className="bg-slate-50 rounded-lg p-4">
                          <p className="text-sm font-semibold text-slate-700 mb-2">Pre-Tax Charges</p>
                          <div className="space-y-1 text-sm">
                            {(() => {
                              // Define meta charge parameters
                              const metaChargeParams = ['pod', 'demurrage', 'sun', 'hand', 'add_man_pwr', 'add_cost',
                                'mathadi_un', 'adh_vhl', 'reattempt', 'appt_chg', 'chq', 'csd', 'cod', 
                                'sp_dlv_area', 'add_machine', 'floor_delivery', 'mall_delivery'];
                              
                              // Separate regular charges and meta charges
                              const regularCharges = [];
                              const metaCharges = [];
                              
                              if (order.seller_charges.fwd_charges.breakdown) {
                                Object.entries(order.seller_charges.fwd_charges.breakdown).forEach(([param, details]) => {
                                  if (metaChargeParams.includes(param)) {
                                    metaCharges.push([param, details]);
                                  } else {
                                    regularCharges.push([param, details]);
                                  }
                                });
                              }
                              
                              return (
                                <>
                                  {/* Display regular charges */}
                                  {regularCharges.map(([param, details]) => (
                                    <div key={param} className="flex justify-between py-1">
                                      <span className="text-slate-600 capitalize">{param.replace(/_/g, ' ')}</span>
                                      <div className="text-right">
                                        <span className="font-semibold text-slate-900">₹{details.seller_value?.toFixed(2)}</span>
                                        {details.method !== 'SAME' && (
                                          <span className="text-xs text-slate-500 ml-2">({details.method})</span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                  
                                  {/* Display meta charges in a special section */}
                                  {metaCharges.length > 0 && metaCharges.some(([, details]) => details.seller_value > 0) && (
                                    <div className="bg-amber-50 rounded p-4 my-2 border border-amber-200">
                                      <h5 className="font-semibold text-slate-700 mb-2 text-sm">Handling Charges Breakdown:</h5>
                                      <div className="space-y-1">
                                        {metaCharges.map(([param, details]) => (
                                          details.seller_value > 0 && (
                                            <div key={param} className="flex justify-between py-1 text-sm">
                                              <span className="text-slate-600 capitalize">{param.replace(/_/g, ' ')}</span>
                                              <div className="text-right">
                                                <span className="font-semibold">₹{details.seller_value?.toFixed(2)}</span>
                                                {details.method !== 'SAME' && (
                                                  <span className="text-xs text-slate-500 ml-2">({details.method})</span>
                                                )}
                                              </div>
                                            </div>
                                          )
                                        ))}
                                      </div>
                                      <div className="flex justify-between pt-2 mt-2 border-t border-amber-300 font-semibold">
                                        <span>Total Handling Charges</span>
                                        <span>₹{metaCharges.reduce((sum, [, details]) => sum + (details.seller_value || 0), 0).toFixed(2)}</span>
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                            
                            {/* PRE-TAX Custom Charges */}
                            {order.seller_charges.fwd_charges.pre_tax_custom_charges?.map((charge, idx) => (
                              <div key={idx} className="flex justify-between py-1 bg-amber-50 px-2 rounded">
                                <span className="text-slate-600 flex items-center gap-1">
                                  {charge.name}
                                  <span className="text-xs bg-amber-200 text-amber-800 px-1 rounded">PRE-TAX</span>
                                </span>
                                <span className="font-semibold text-slate-900">₹{charge.amount?.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                          
                          <div className="flex justify-between pt-2 mt-2 border-t border-slate-300 font-semibold">
                            <span>Pre-Tax Freight Total</span>
                            <span>₹{order.seller_charges.fwd_charges.pre_tax_freight?.toFixed(2)}</span>
                          </div>
                        </div>
                        
                        {/* GST */}
                        <div className="flex justify-between py-2 border-b bg-green-50 px-3 rounded">
                          <span className="text-slate-700 font-medium">GST ({order.seller_charges.fwd_charges.gst_percent}%)</span>
                          <span className="font-bold text-green-700">₹{order.seller_charges.fwd_charges.gst?.toFixed(2)}</span>
                        </div>
                        
                        {/* Separate Non-Taxable Charges */}
                        {(order.seller_charges.fwd_charges.fm > 0 || order.seller_charges.fwd_charges.lm > 0) && (
                          <div className="bg-slate-50 rounded-lg p-4">
                            <p className="text-sm font-semibold text-slate-700 mb-2">Separate Charges (Non-Taxable)</p>
                            {order.seller_charges.fwd_charges.fm > 0 && (
                              <div className="flex justify-between py-1 text-sm">
                                <span className="text-slate-600">First Mile (FM)</span>
                                <span className="font-semibold">₹{order.seller_charges.fwd_charges.fm?.toFixed(2)}</span>
                              </div>
                            )}
                            {order.seller_charges.fwd_charges.lm > 0 && (
                              <div className="flex justify-between py-1 text-sm">
                                <span className="text-slate-600">Last Mile (LM)</span>
                                <span className="font-semibold">₹{order.seller_charges.fwd_charges.lm?.toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* POST-TAX Custom Charges */}
                        {order.seller_charges.fwd_charges.post_tax_custom_charges?.length > 0 && (
                          <div className="bg-purple-50 rounded-lg p-4">
                            <p className="text-sm font-semibold text-slate-700 mb-2">Post-Tax Charges</p>
                            {order.seller_charges.fwd_charges.post_tax_custom_charges.map((charge, idx) => (
                              <div key={idx} className="flex justify-between py-1 text-sm">
                                <span className="text-slate-600 flex items-center gap-1">
                                  {charge.name}
                                  <span className="text-xs bg-purple-200 text-purple-800 px-1 rounded">POST-TAX</span>
                                </span>
                                <span className="font-semibold">₹{charge.amount?.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="flex justify-between pt-2 font-bold text-lg border-t-2 border-blue-300">
                          <span>Forward Total</span>
                          <span className="text-blue-700">₹{order.seller_charges.fwd_charges.total?.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* RTO Charges */}
                  {order.seller_charges.rto_charges && order.seller_charges.rto_charges.total > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-bold">RTO</span>
                        RTO (Return) Charges Breakdown
                      </h4>
                      
                      <div className="space-y-3 pl-4 border-l-2 border-red-200">
                        <div className="bg-slate-50 rounded-lg p-4">
                          <p className="text-sm font-semibold text-slate-700 mb-2">Pre-Tax Charges</p>
                          <div className="space-y-1 text-sm">
                            {(() => {
                              // Define meta charge parameters
                              const metaChargeParams = ['pod', 'demurrage', 'sun', 'hand', 'add_man_pwr', 'add_cost',
                                'mathadi_un', 'adh_vhl', 'reattempt', 'appt_chg', 'chq', 'csd', 'cod', 
                                'sp_dlv_area', 'add_machine', 'floor_delivery', 'mall_delivery'];
                              
                              // Separate regular charges and meta charges
                              const regularCharges = [];
                              const metaCharges = [];
                              
                              if (order.seller_charges.rto_charges.breakdown) {
                                Object.entries(order.seller_charges.rto_charges.breakdown).forEach(([param, details]) => {
                                  if (metaChargeParams.includes(param)) {
                                    metaCharges.push([param, details]);
                                  } else {
                                    regularCharges.push([param, details]);
                                  }
                                });
                              }
                              
                              return (
                                <>
                                  {/* Display regular charges */}
                                  {regularCharges.map(([param, details]) => (
                                    <div key={param} className="flex justify-between py-1">
                                      <span className="text-slate-600 capitalize">{param.replace(/_/g, ' ')}</span>
                                      <span className="font-semibold text-slate-900">₹{details.seller_value?.toFixed(2)}</span>
                                    </div>
                                  ))}
                                  
                                  {/* Display meta charges in a special section */}
                                  {metaCharges.length > 0 && metaCharges.some(([, details]) => details.seller_value > 0) && (
                                    <div className="bg-amber-50 rounded p-4 my-2 border border-amber-200">
                                      <h5 className="font-semibold text-slate-700 mb-2 text-sm">Handling Charges Breakdown:</h5>
                                      <div className="space-y-1">
                                        {metaCharges.map(([param, details]) => (
                                          details.seller_value > 0 && (
                                            <div key={param} className="flex justify-between py-1 text-sm">
                                              <span className="text-slate-600 capitalize">{param.replace(/_/g, ' ')}</span>
                                              <span className="font-semibold">₹{details.seller_value?.toFixed(2)}</span>
                                            </div>
                                          )
                                        ))}
                                      </div>
                                      <div className="flex justify-between pt-2 mt-2 border-t border-amber-300 font-semibold">
                                        <span>Total Handling Charges</span>
                                        <span>₹{metaCharges.reduce((sum, [, details]) => sum + (details.seller_value || 0), 0).toFixed(2)}</span>
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          
                          <div className="flex justify-between pt-2 mt-2 border-t border-slate-300 font-semibold">
                            <span>Pre-Tax Freight Total</span>
                            <span>₹{order.seller_charges.rto_charges.pre_tax_freight?.toFixed(2)}</span>
                          </div>
                        </div>
                        
                        <div className="flex justify-between py-2 border-b bg-green-50 px-3 rounded">
                          <span className="text-slate-700 font-medium">GST ({order.seller_charges.rto_charges.gst_percent}%)</span>
                          <span className="font-bold text-green-700">₹{order.seller_charges.rto_charges.gst?.toFixed(2)}</span>
                        </div>
                        
                        <div className="flex justify-between pt-2 font-bold text-lg border-t-2 border-red-300">
                          <span>RTO Total</span>
                          <span className="text-red-700">₹{order.seller_charges.rto_charges.total?.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Final Summary */}
                  <div className="bg-slate-100 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-lg">
                      <span className="font-semibold text-slate-700">Subtotal</span>
                      <span className="font-bold">₹{order.seller_charges.subtotal?.toFixed(2)}</span>
                    </div>
                    
                    {order.seller_charges.management_fee?.enabled && (
                      <div className="flex justify-between text-base border-t pt-2">
                        <span className="text-slate-600">
                          Management Fee ({order.seller_charges.management_fee.percentage}%)
                        </span>
                        <span className="font-semibold text-[#00A699]">
                          + ₹{order.seller_charges.management_fee.amount?.toFixed(2)}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex justify-between text-xl pt-2 border-t-2 border-slate-400">
                      <span className="font-bold text-slate-900">Final Amount</span>
                      <span className="font-bold text-[#00A699]">
                        ₹{order.seller_charges.final_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="text-xs text-slate-500 space-y-1 bg-slate-50 p-4 rounded">
                    <p>Calculated on: {formatDateIST(order.seller_charges_calculated_at, 'full')}</p>
                    {order.wallet_transaction_id && (
                      <p>Transaction ID: {order.wallet_transaction_id}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Box Details */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" style={{color: '#00A699'}} />
                Box Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {order.boxes && order.boxes.length > 0 ? order.boxes.map((box, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-slate-50">
                    <p className="font-semibold text-slate-700 mb-2">Box {index + 1}</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-slate-500">Dimensions: </span>
                        <span className="text-slate-900">{box.length} × {box.width} × {box.height} cm</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Weight: </span>
                        <span className="text-slate-900">{box.weight} kg</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-slate-500">Quantity: </span>
                        <span className="text-slate-900">{box.quantity}</span>
                      </div>
                    </div>
                  </div>
                )) : (
                  <p className="text-slate-500 text-center py-4">No box details available</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Locations */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
                <span className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" style={{color: '#00A699'}} />
                  Pickup & Drop Locations
                </span>
                {order.origin_zone && order.dest_zone && (
                  <span
                    title="Shipping lane — pickup state's zone → drop state's zone"
                    className="inline-flex items-center gap-1 rounded-full bg-[#00A699]/10 text-[#00A699] text-xs font-medium px-2.5 py-0.5"
                  >
                    {order.origin_zone} <span className="text-slate-400">→</span> {order.dest_zone}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Pickup Location */}
              {order.pickup_location && (
                <div className="border rounded-lg p-4 bg-green-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className="w-4 h-4 text-green-600" />
                    <p className="font-semibold text-green-800">Pickup Location</p>
                  </div>
                  <p className="font-medium text-slate-900">{order.pickup_location.facility_name}</p>
                  <p className="text-sm text-slate-600">{order.pickup_location.address_line}</p>
                  <p className="text-sm text-slate-600">
                    {order.pickup_location.city}, {order.pickup_location.state} - {order.pickup_location.pincode}
                  </p>
                  <p className="text-sm mt-1 flex items-center gap-1.5">
                    <span className="text-slate-500">Zone:</span>
                    <span className="inline-flex items-center rounded bg-[#00A699]/10 text-[#00A699] text-xs font-semibold px-2 py-0.5">{order.origin_zone || "—"}</span>
                  </p>
                  <div className="mt-2 text-sm">
                    <p className="text-slate-700">Contact: {order.pickup_location.contact_person_name}</p>
                    <p className="text-slate-600">{order.pickup_location.contact_phone}</p>
                  </div>
                </div>
              )}

              {/* Drop Location */}
              {order.drop_location && (
                <div className="border rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    <p className="font-semibold text-blue-800">Drop Location</p>
                  </div>
                  <p className="font-medium text-slate-900">{order.drop_location.company_name}</p>
                  <p className="text-sm text-slate-600">{order.drop_location.address_line}</p>
                  <p className="text-sm text-slate-600">
                    {order.drop_location.city}, {order.drop_location.state} - {order.drop_location.pincode}
                  </p>
                  <p className="text-sm mt-1 flex items-center gap-1.5">
                    <span className="text-slate-500">Zone:</span>
                    <span className="inline-flex items-center rounded bg-[#00A699]/10 text-[#00A699] text-xs font-semibold px-2 py-0.5">{order.dest_zone || "—"}</span>
                  </p>
                  <div className="mt-2 text-sm">
                    <p className="text-slate-700">Contact: {order.drop_location.contact_person_name}</p>
                    <p className="text-slate-600">{order.drop_location.drop_contact}</p>
                    {order.drop_location.email && (
                      <p className="text-slate-600">{order.drop_location.email}</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Edit History (pre-LRN order edits) */}
        {order.edit_history && order.edit_history.length > 0 && (
          <div className="mt-6">
            <OrderEditHistory history={order.edit_history} />
          </div>
        )}

        {/* Estimated & Promised Delivery Dates */}
        {((order.estimated_dates && order.estimated_dates.length > 0) || 
          (order.promised_delivery_dates && order.promised_delivery_dates.length > 0)) && (
          <div className="grid grid-cols-2 gap-6 mt-6">
            {order.estimated_dates && order.estimated_dates.length > 0 && (
              <Card className="border-0 shadow-lg bg-blue-50">
                <CardContent className="pt-4">
                  <p className="text-sm font-semibold text-blue-800 mb-2">Estimated Delivery Dates</p>
                  <div className="space-y-2">
                    {order.estimated_dates.map((entry, idx) => {
                      const dateValue = entry?.date || entry;
                      const fetchedAt = entry?.fetched_at;
                      return (
                        <div key={idx} className="text-sm">
                          <p className="text-blue-700 font-medium">{formatDateIST(dateValue, 'full')}</p>
                          {fetchedAt && (
                            <p className="text-blue-500 text-xs">Received: {formatDateIST(fetchedAt, 'full')}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            {order.promised_delivery_dates && order.promised_delivery_dates.length > 0 && (
              <Card className="border-0 shadow-lg bg-green-50">
                <CardContent className="pt-4">
                  <p className="text-sm font-semibold text-green-800 mb-2">Promised Delivery Dates</p>
                  <div className="space-y-2">
                    {order.promised_delivery_dates.map((entry, idx) => {
                      const dateValue = entry?.date || entry;
                      const fetchedAt = entry?.fetched_at;
                      return (
                        <div key={idx} className="text-sm">
                          <p className="text-green-700 font-medium">{formatDateIST(dateValue, 'full')}</p>
                          {fetchedAt && (
                            <p className="text-green-500 text-xs">Received: {formatDateIST(fetchedAt, 'full')}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Combined Order Tracking Journey */}
        <Card className="border-0 shadow-lg mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" style={{color: '#00A699'}} />
              Order Tracking Journey
              {order.scan_history && order.scan_history.length > 0 && (
                <Badge variant="secondary" className="ml-2">{order.scan_history.length} scans</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Status Timeline - only show event time if available */}
            <div className="relative">
              {(order.status_history || []).slice().reverse().map((entry, index) => {
                const isLast = index === (order.status_history?.length || 0) - 1;
                const statusColor = STATUS_COLORS[entry.status] || 'bg-gray-100 text-gray-800';
                
                return (
                  <div key={index} className="flex gap-4 pb-6 relative">
                    {/* Timeline connector */}
                    {!isLast && (
                      <div className="absolute left-[11px] top-6 w-0.5 h-full bg-slate-200" />
                    )}
                    
                    {/* Status dot */}
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      index === 0 ? 'bg-[#00A699]' : 'bg-slate-300'
                    }`}>
                      {index === 0 ? (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`${statusColor} border-0`}>
                          {entry.status?.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      {/* Timestamps */}
                      <div className="text-sm mt-1 space-y-0.5">
                        {entry.event_timestamp && (
                          <p className="text-slate-700">
                            <span className="text-slate-500">Event Time:</span> {formatDateIST(entry.event_timestamp, 'full')}
                          </p>
                        )}
                        {entry.timestamp && (
                          <p className="text-slate-500 text-xs">
                            <span>System Updated:</span> {formatDateIST(entry.timestamp, 'full')}
                          </p>
                        )}
                      </div>
                      {entry.remarks && (
                        <p className="text-sm text-slate-500 mt-1 italic">{entry.remarks}</p>
                      )}
                      {entry.updated_by && (
                        <p className="text-xs text-slate-400 mt-1">
                          Updated by: {entry.updated_by}
                          {entry.user_role && <span className="ml-1">({entry.user_role})</span>}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Scan History Table - if available */}
            {order.scan_history && order.scan_history.length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Courier Scan Details</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Remark</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {order.scan_history.slice().reverse().map((scan, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-4 py-3 align-middle">
                            <Badge variant="outline" className="text-xs">
                              {scan.status?.replace(/_/g, ' ')}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 align-middle text-slate-700">{scan.location || '-'}</td>
                          <td className="px-4 py-3 align-middle text-slate-600 italic">{scan.scan_remark || '-'}</td>
                          <td className="px-4 py-3 align-middle text-slate-700">
                            {scan.scan_timestamp ? (
                              <div className="whitespace-nowrap">
                                <div>{formatToIST(scan.scan_timestamp, false)}</div>
                                <div className="text-xs text-slate-500">{formatToIST(scan.scan_timestamp).split(', ')[1]}</div>
                              </div>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Contact Tracking Card */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b">
            <CardTitle className="flex items-center gap-2 text-lg text-indigo-800">
              <MessageSquare className="w-5 h-5" />
              Contact Tracking
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Contact Persons Section */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Pickup Person */}
              <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Pickup Person
                  </h4>
                  {!editingPickup ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingPickup(true)}
                      className="h-8 text-blue-600 hover:text-blue-800"
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  ) : (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSavePickupPerson}
                        disabled={savingPickup}
                        className="h-8 text-green-600 hover:text-green-800"
                      >
                        {savingPickup ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingPickup(false);
                          setPickupPerson(order.pickup_person || { name: "", phone: "", vehicle_number: "" });
                        }}
                        className="h-8 text-slate-500"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                
                {editingPickup ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-slate-600">Name</Label>
                      <Input
                        value={pickupPerson.name}
                        onChange={(e) => setPickupPerson(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter name"
                        className="h-9 mt-1"
                        list="pickup-name-suggestions"
                      />
                      <datalist id="pickup-name-suggestions">
                        {contactSuggestions.filter(s => s.type === "pickup" && s.name).map((s, i) => (
                          <option key={i} value={s.name} />
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Phone</Label>
                      <Input
                        value={pickupPerson.phone}
                        onChange={(e) => setPickupPerson(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="Enter phone number"
                        className="h-9 mt-1"
                        list="pickup-phone-suggestions"
                      />
                      <datalist id="pickup-phone-suggestions">
                        {contactSuggestions.filter(s => s.type === "pickup" && s.phone).map((s, i) => (
                          <option key={i} value={s.phone} />
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Vehicle Number</Label>
                      <Input
                        value={pickupPerson.vehicle_number}
                        onChange={(e) => setPickupPerson(prev => ({ ...prev, vehicle_number: e.target.value }))}
                        placeholder="Enter vehicle number"
                        className="h-9 mt-1"
                      />
                    </div>
                    {/* Quick suggestions */}
                    {contactSuggestions.filter(s => s.type === "pickup").length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-slate-500 mb-1">Recent contacts:</p>
                        <div className="flex flex-wrap gap-1">
                          {contactSuggestions.filter(s => s.type === "pickup").slice(0, 3).map((s, i) => (
                            <Button
                              key={i}
                              variant="outline"
                              size="sm"
                              className="h-6 text-xs"
                              onClick={() => applySuggestion(s, "pickup")}
                            >
                              {s.name || s.phone}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 text-sm">
                    {order.pickup_person?.name || order.pickup_person?.phone ? (
                      <>
                        {order.pickup_person?.name && (
                          <p className="flex items-center gap-2">
                            <User className="w-4 h-4 text-blue-500" />
                            <span className="font-medium">{order.pickup_person.name}</span>
                          </p>
                        )}
                        {order.pickup_person?.phone && (
                          <p className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-blue-500" />
                            <a href={`tel:${order.pickup_person.phone}`} className="text-blue-600 hover:underline">
                              {order.pickup_person.phone}
                            </a>
                          </p>
                        )}
                        {order.pickup_person?.vehicle_number && (
                          <p className="flex items-center gap-2">
                            <Car className="w-4 h-4 text-blue-500" />
                            <span>{order.pickup_person.vehicle_number}</span>
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-slate-400 italic">No pickup person assigned</p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Delivery Person */}
              <div className="bg-gradient-to-br from-green-50 to-slate-50 rounded-lg p-4 border border-green-100">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-green-800 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Delivery Person
                  </h4>
                  {!editingDelivery ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingDelivery(true)}
                      className="h-8 text-green-600 hover:text-green-800"
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  ) : (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSaveDeliveryPerson}
                        disabled={savingDelivery}
                        className="h-8 text-green-600 hover:text-green-800"
                      >
                        {savingDelivery ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingDelivery(false);
                          setDeliveryPerson(order.delivery_person || { name: "", phone: "", vehicle_number: "" });
                        }}
                        className="h-8 text-slate-500"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                
                {editingDelivery ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-slate-600">Name</Label>
                      <Input
                        value={deliveryPerson.name}
                        onChange={(e) => setDeliveryPerson(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter name"
                        className="h-9 mt-1"
                        list="delivery-name-suggestions"
                      />
                      <datalist id="delivery-name-suggestions">
                        {contactSuggestions.filter(s => s.type === "delivery" && s.name).map((s, i) => (
                          <option key={i} value={s.name} />
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Phone</Label>
                      <Input
                        value={deliveryPerson.phone}
                        onChange={(e) => setDeliveryPerson(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="Enter phone number"
                        className="h-9 mt-1"
                        list="delivery-phone-suggestions"
                      />
                      <datalist id="delivery-phone-suggestions">
                        {contactSuggestions.filter(s => s.type === "delivery" && s.phone).map((s, i) => (
                          <option key={i} value={s.phone} />
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Vehicle Number</Label>
                      <Input
                        value={deliveryPerson.vehicle_number}
                        onChange={(e) => setDeliveryPerson(prev => ({ ...prev, vehicle_number: e.target.value }))}
                        placeholder="Enter vehicle number"
                        className="h-9 mt-1"
                      />
                    </div>
                    {/* Quick suggestions */}
                    {contactSuggestions.filter(s => s.type === "delivery").length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-slate-500 mb-1">Recent contacts:</p>
                        <div className="flex flex-wrap gap-1">
                          {contactSuggestions.filter(s => s.type === "delivery").slice(0, 3).map((s, i) => (
                            <Button
                              key={i}
                              variant="outline"
                              size="sm"
                              className="h-6 text-xs"
                              onClick={() => applySuggestion(s, "delivery")}
                            >
                              {s.name || s.phone}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 text-sm">
                    {order.delivery_person?.name || order.delivery_person?.phone ? (
                      <>
                        {order.delivery_person?.name && (
                          <p className="flex items-center gap-2">
                            <User className="w-4 h-4 text-green-500" />
                            <span className="font-medium">{order.delivery_person.name}</span>
                          </p>
                        )}
                        {order.delivery_person?.phone && (
                          <p className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-green-500" />
                            <a href={`tel:${order.delivery_person.phone}`} className="text-green-600 hover:underline">
                              {order.delivery_person.phone}
                            </a>
                          </p>
                        )}
                        {order.delivery_person?.vehicle_number && (
                          <p className="flex items-center gap-2">
                            <Car className="w-4 h-4 text-green-500" />
                            <span>{order.delivery_person.vehicle_number}</span>
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-slate-400 italic">No delivery person assigned</p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Conversation Log Section */}
            <div className="border-t pt-6">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4" />
                Conversation Log
              </h4>
              
              {/* Chat-like Log Display */}
              <div className="bg-slate-50 rounded-lg p-4 mb-4 max-h-80 overflow-y-auto">
                {order.contact_logs && order.contact_logs.length > 0 ? (
                  <div className="space-y-3">
                    {order.contact_logs.map((log, idx) => (
                      <div
                        key={log.id || idx}
                        className={`flex ${log.contact_type === 'pickup' ? 'justify-start' : 'justify-end'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg p-3 shadow-sm ${
                            log.contact_type === 'pickup'
                              ? 'bg-blue-100 border border-blue-200'
                              : 'bg-green-100 border border-green-200'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <Badge
                              variant="outline"
                              className={`text-xs ${
                                log.contact_type === 'pickup'
                                  ? 'border-blue-300 text-blue-700'
                                  : 'border-green-300 text-green-700'
                              }`}
                            >
                              {log.contact_type === 'pickup' ? '📦 Pickup' : '🚚 Delivery'}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {log.status}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteContactLog(log.id)}
                              className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{log.notes}</p>
                          <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                            <span>{log.logged_by_name || 'Unknown'}</span>
                            <span>{formatDateIST(log.timestamp, 'short')}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={chatEndRef} />
                  </div>
                ) : (
                  <p className="text-center text-slate-400 py-8">No conversation logs yet</p>
                )}
              </div>
              
              {/* Add New Log Entry */}
              <div className="bg-white border rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <div>
                    <Label className="text-xs text-slate-600">Contact Type</Label>
                    <Select
                      value={newLogEntry.contact_type}
                      onValueChange={(value) => setNewLogEntry(prev => ({ ...prev, contact_type: value }))}
                    >
                      <SelectTrigger className="h-9 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pickup">📦 Pickup Person</SelectItem>
                        <SelectItem value="delivery">🚚 Delivery Person</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-600">Status</Label>
                    <Select
                      value={newLogEntry.status}
                      onValueChange={(value) => setNewLogEntry(prev => ({ ...prev, status: value }))}
                    >
                      <SelectTrigger className="h-9 mt-1">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {contactLogStatuses.map((status) => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Textarea
                    value={newLogEntry.notes}
                    onChange={(e) => setNewLogEntry(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Enter conversation notes..."
                    className="flex-1 min-h-[60px] resize-none"
                  />
                  <Button
                    onClick={handleAddContactLog}
                    disabled={addingLog || !newLogEntry.status || !newLogEntry.notes.trim()}
                    className="self-end"
                  >
                    {addingLog ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Video Footage Card */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-rose-50 to-orange-50 border-b">
            <CardTitle className="flex items-center gap-2 text-lg text-rose-800">
              <Video className="w-5 h-5" />
              Video Footage
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Pickup Videos */}
              <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Pickup Videos
                  </h4>
                  <div>
                    <input
                      type="file"
                      ref={pickupVideoRef}
                      onChange={handleUploadPickupVideo}
                      accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,video/3gpp,video/x-matroska"
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => pickupVideoRef.current?.click()}
                      disabled={uploadingPickupVideo}
                      className="h-8"
                    >
                      {uploadingPickupVideo ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Upload className="w-4 h-4 mr-1" />
                      )}
                      Upload
                    </Button>
                  </div>
                </div>
                
                {order.pickup_videos && order.pickup_videos.length > 0 ? (
                  <div className="space-y-2">
                    {order.pickup_videos.map((video, idx) => (
                      <div
                        key={video.id || idx}
                        className="flex items-center justify-between bg-white rounded-lg p-3 border"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Play className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{video.filename}</p>
                            <p className="text-xs text-slate-500">
                              {formatFileSize(video.size)} • {formatDateIST(video.uploaded_at, 'short')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                            className="h-8 w-8 p-0"
                          >
                            <a href={video.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4 text-blue-600" />
                            </a>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePickupVideo(video.id)}
                            className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-400">
                    <Video className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No pickup videos uploaded</p>
                    <p className="text-xs mt-1">Upload MP4, MOV, AVI, WebM (max 100MB)</p>
                  </div>
                )}
              </div>
              
              {/* Delivery Videos */}
              <div className="bg-gradient-to-br from-green-50 to-slate-50 rounded-lg p-4 border border-green-100">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-green-800 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Delivery Videos
                  </h4>
                  <div>
                    <input
                      type="file"
                      ref={deliveryVideoRef}
                      onChange={handleUploadDeliveryVideo}
                      accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,video/3gpp,video/x-matroska"
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deliveryVideoRef.current?.click()}
                      disabled={uploadingDeliveryVideo}
                      className="h-8"
                    >
                      {uploadingDeliveryVideo ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Upload className="w-4 h-4 mr-1" />
                      )}
                      Upload
                    </Button>
                  </div>
                </div>
                
                {order.delivery_videos && order.delivery_videos.length > 0 ? (
                  <div className="space-y-2">
                    {order.delivery_videos.map((video, idx) => (
                      <div
                        key={video.id || idx}
                        className="flex items-center justify-between bg-white rounded-lg p-3 border"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                            <Play className="w-5 h-5 text-green-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{video.filename}</p>
                            <p className="text-xs text-slate-500">
                              {formatFileSize(video.size)} • {formatDateIST(video.uploaded_at, 'short')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                            className="h-8 w-8 p-0"
                          >
                            <a href={video.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4 text-green-600" />
                            </a>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDeliveryVideo(video.id)}
                            className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-400">
                    <Video className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No delivery videos uploaded</p>
                    <p className="text-xs mt-1">Upload MP4, MOV, AVI, WebM (max 100MB)</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Red Flags Section */}
        {order.red_flags && order.red_flags.length > 0 && (
          <Card className="border-red-200 bg-red-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                <Flag className="w-5 h-5" />
                Active Red Flags ({order.red_flags.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {order.red_flags.map((flag) => (
                  <div
                    key={flag.id}
                    className="flex items-start justify-between bg-white rounded-lg p-3 border border-red-200"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge 
                          className={
                            flag.priority === 'critical' ? 'bg-red-100 text-red-700' :
                            flag.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                            flag.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-slate-100 text-slate-700'
                          }
                        >
                          {flag.priority?.toUpperCase()}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {flag.flag_type_name}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-slate-800">{flag.title}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        ID: {flag.id} • Created: {formatDateIST(flag.created_at, 'short')}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => navigate(`/admin/red-flags?flag_id=${flag.id}`)}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Manifest & LRN API call logs (Delhivery) — admin/super-admin only */}
        <ManifestApiLogs orderId={orderId} />
      </div>
    </AdminLayout>
  );
};

export default OrderDetail;
